/**
 * Asking a question, in the product's own voice.
 *
 * Two places used the browser's own `prompt()` and `confirm()`: naming a new
 * colour role, and clearing the shelf. Both work, and both look like a security
 * warning from 1998 pinned to the top of the window, in the operating system's
 * font, with a title bar that says the site's hostname. It reads as something
 * happening TO the page rather than part of it.
 *
 * Built on `<dialog>` rather than a div with a high z-index, which gets the
 * awkward parts for free and gets them right: focus moves in and comes back out
 * to whatever opened it, Escape closes, the rest of the page is inert while it
 * is open, and it sits in the top layer so nothing can accidentally cover it.
 *
 * Every function here resolves rather than throws when somebody backs out. A
 * cancelled question is a normal answer, not an error.
 */

export interface AskFields {
	/** The one-line question, which is the heading. */
	title: string;
	/** Why they are being asked, when the title cannot carry it alone. */
	body?: string;
	/** The word on the button that does the thing. Never "OK". */
	confirm?: string;
	/** For a question whose answer is destructive. */
	danger?: boolean;
}

export interface AskText extends AskFields {
	/** The label above the field, for screen readers and for everyone else. */
	label: string;
	placeholder?: string;
	value?: string;
	/**
	 * Checked as they type. Return a sentence explaining the problem, or null.
	 * Validating here rather than after closing means the answer can be fixed in
	 * place, instead of the dialog vanishing and an error appearing somewhere
	 * else on the page with the typed text gone.
	 */
	check?: (value: string) => string | null;
	/**
	 * A box rather than a line, for an answer that is a document.
	 *
	 * Pasting a sprite is the only caller. It is a flag on this rather than a
	 * second function because everything else about the two is the same: the same
	 * validation as you type, the same disabled button until it passes, the same
	 * way out.
	 */
	multiline?: boolean;
}

interface Frame {
	dialog: HTMLDialogElement;
	form: HTMLFormElement;
	row: HTMLDivElement;
	cancel: HTMLButtonElement;
	go: HTMLButtonElement;
}

/** The shell both questions share: a heading, some body, and two buttons. */
function frame(fields: AskFields): Frame {
	const dialog = document.createElement("dialog");
	dialog.className = "sheetbox";

	const form = document.createElement("form");
	form.method = "dialog";

	const title = document.createElement("h2");
	title.textContent = fields.title;
	form.append(title);

	if (fields.body) {
		const body = document.createElement("p");
		body.className = "sheet-body";
		body.textContent = fields.body;
		form.append(body);
	}

	const row = document.createElement("div");
	row.className = "sheet-row";

	const cancel = document.createElement("button");
	cancel.type = "button";
	cancel.className = "btn";
	cancel.textContent = "Cancel";

	const go = document.createElement("button");
	go.type = "submit";
	go.className = fields.danger ? "go small danger" : "go small";
	go.textContent = fields.confirm ?? "Done";
	row.append(cancel, go);
	dialog.append(form);

	return { dialog, form, row, cancel, go };
}

/**
 * Open it, wait for an answer, tidy up.
 *
 * Every way out is listened for by name, which is not belt and braces. All of
 * this used to hang off the `close` event, and `close` is the one that did not
 * arrive: in Chrome, submitting a form with `method="dialog"` closes the dialog
 * and sets `returnValue` without firing it. Pressing the confirm button
 * dismissed the box and the promise never settled, so the colour was never
 * added and the sprite never opened, silently and with nothing in the console.
 *
 * So each route says so itself: the form's own `submit` carries the yes, and
 * the Cancel button, `cancel` (Escape) and `close` each carry a no. Whichever
 * arrives first wins and the rest are ignored, because finishing closes the
 * dialog and that would otherwise read as a second, contradictory answer.
 */
function run<T>(parts: Frame, said: () => T, backedOut: T): Promise<T> {
	const { dialog, form, cancel } = parts;
	return new Promise((resolve) => {
		let settled = false;
		const finish = (value: T) => {
			if (settled) return;
			settled = true;
			// close() on an already-closed dialog is a no-op, so this is safe on
			// every path including the one where the browser closed it for us.
			dialog.close();
			dialog.remove();
			resolve(value);
		};

		// Yes. Handled here rather than left to method="dialog", so the answer is
		// read the same way whatever the browser does with the form.
		form.addEventListener("submit", (e) => {
			e.preventDefault();
			finish(said());
		});

		// Every way of backing out, named individually. `close` alone is not
		// enough: it is the event this all used to hang off, and it is the one
		// that did not arrive.
		cancel.addEventListener("click", () => finish(backedOut));
		dialog.addEventListener("cancel", () => finish(backedOut));
		dialog.addEventListener("close", () => finish(backedOut));

		document.body.append(dialog);
		dialog.showModal();
	});
}

/** A yes or no question. Resolves false if they back out any way at all. */
export function askYesNo(fields: AskFields): Promise<boolean> {
	const parts = frame(fields);
	parts.form.append(parts.row);
	return run(parts, () => true, false);
}

/** A question with a typed answer. Resolves null if they back out. */
export function askText(fields: AskText): Promise<string | null> {
	const parts = frame(fields);
	const { form, row, go } = parts;

	const label = document.createElement("label");
	label.className = "sheet-label";
	label.textContent = fields.label;

	const input = fields.multiline
		? document.createElement("textarea")
		: Object.assign(document.createElement("input"), { type: "text" });
	input.className = fields.multiline ? "sheet-input sheet-area" : "sheet-input";
	input.value = fields.value ?? "";
	if (fields.placeholder) input.placeholder = fields.placeholder;
	input.autocomplete = "off";
	input.spellcheck = false;

	const problem = document.createElement("p");
	problem.className = "sheet-problem";
	problem.hidden = true;
	problem.id = "sheet-problem";
	input.setAttribute("aria-describedby", problem.id);

	/*
	 * The submit button stays disabled until there is a usable answer, so the
	 * only way to close this with a bad value is to cancel.
	 *
	 * An empty field is not wrong yet, it is just empty, so it disables the
	 * button without turning red. Telling somebody their answer is invalid
	 * before they have given one is a scolding, not a hint.
	 */
	const judge = () => {
		const typed = input.value.trim();
		const said = typed === "" ? null : (fields.check?.(typed) ?? null);
		problem.textContent = said ?? "";
		problem.hidden = said === null;
		input.setAttribute("aria-invalid", String(said !== null));
		go.disabled = typed === "" || said !== null;
	};
	input.addEventListener("input", judge);
	judge();

	label.append(input);
	form.append(label, problem, row);

	// showModal puts the cursor here rather than on the first button, because
	// somebody who opened this came to type.
	input.autofocus = true;

	return run<string | null>(parts, () => input.value.trim() || null, null);
}
